import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { type PlatformUser, isPlatformUser } from '@/stores/auth'
import { useAuthStore, useUser } from '@/stores/authStore'
import { ChevronsUpDown, LogOut, SlidersHorizontal } from 'lucide-react'
import { FooterItemLink } from './FooterItemLink'
import type { FooterItem } from './useFooterItems'

export function MoreMenu({ items, onCustomize }: { items: FooterItem[]; onCustomize: () => void }) {
	const user = useUser() as PlatformUser | null
	const role = user?.role?.name

	return (
		<DropdownMenu>
			<Tooltip>
				<TooltipTrigger asChild>
					<DropdownMenuTrigger asChild>
						<button
							type='button'
							aria-label='More'
							data-testid='sidebar-bar-more'
							className='flex h-7 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-foreground'>
							<ChevronsUpDown className='size-(--icon-standalone)' aria-hidden />
						</button>
					</DropdownMenuTrigger>
				</TooltipTrigger>
				<TooltipContent side='top'>More</TooltipContent>
			</Tooltip>
			<DropdownMenuContent
				side='top'
				align='end'
				sideOffset={8}
				className='z-100 w-(--sidebar-width)'
				data-testid='sidebar-bar-more-menu'>
				<div className='flex items-center gap-2 py-1.5 pr-1 pl-2'>
					<div className='flex min-w-0 flex-1 flex-col'>
						<span className='truncate text-body font-semibold' data-testid='sidebar-account-name'>
							{user?.name || 'User'}
						</span>
						{role && (
							<span
								className='truncate text-control text-muted-foreground capitalize'
								data-testid='sidebar-account-role'>
								{role}
							</span>
						)}
					</div>
					{isPlatformUser(user) && (
						<Tooltip>
							<TooltipTrigger asChild>
								<DropdownMenuItem
									onSelect={onCustomize}
									aria-label='Customize'
									data-testid='sidebar-customize-trigger'
									className='size-7 shrink-0 justify-center border p-0'>
									<SlidersHorizontal aria-hidden />
								</DropdownMenuItem>
							</TooltipTrigger>
							<TooltipContent side='top'>Customize</TooltipContent>
						</Tooltip>
					)}
				</div>
				<DropdownMenuSeparator />
				{items.map(item => (
					<DropdownMenuItem key={item.id} asChild>
						<FooterItemLink item={item} data-testid={`sidebar-bar-menu-${item.id}`}>
							<item.icon aria-hidden />
							{item.title}
							{item.isSettingsTab && <DropdownMenuShortcut className='tracking-normal'>Settings</DropdownMenuShortcut>}
						</FooterItemLink>
					</DropdownMenuItem>
				))}
				{items.length > 0 && <DropdownMenuSeparator />}
				<DropdownMenuItem
					variant='destructive'
					onSelect={() => useAuthStore.getState().logout()}
					data-testid='sidebar-signout'>
					<LogOut aria-hidden />
					Sign out
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
